import { Me, Team } from '@yandex-int/k-common';
import { ReduxTeacherStore } from 'client/bundles/lab/typings';
import { format } from 'date-fns';
import { getMe } from 'platform-components/src/components/user/selectors';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import ym from 'react-yandex-metrika';

import { getCurrentYearCourseId } from 'common.components/course/selectors';
import { getCurrentClass } from 'common.components/user/selectors';

import { CalendarPlanShowcaseRouteParams } from '../typings';

interface MetrikaParams {
    block_type?: MetrikaBlockTypeParam; // Тип блока на странице
    click_from?: MetrikaClickFromParam; // Место где произошёл клик по элементу
    course_id?: number; // Номер курса текущего года
    lesson_type?: MetrikaBlockTypeParam; // Тип урока на странице
    lesson_id?: string; // Номер урока
    main_block_name?: MetrikaBlockNameParam; // Имя блока на странице
    main_block_number?: string; // Порядковый номер блока на странице
    main_element_number?: string; // Порядковый номер плашки в блоке
    period_id?: string; // Идентификатор периода (фактически одного слайда витрины)
    subject_slug?: string; // Слаг предмета
    teacher_id?: string; // Идентификатор учителя
    team_id?: number; // Идентификатор команды (класса)
    team_level?: string; // Класс
    template_id?: string; // Номер шаблона страницы
}

enum MetrikaBlockTypeParam {
    ARRAY_OF_LESSONS = 'arrayOfLessons',
    READY_LESSONS = 'readyLessons',
    PROBLEMS = 'problems',
}

enum MetrikaBlockNameParam {
    MAIN_THEME = 'main_theme', // Главная тема (в том числе блок с ХГЗ)
}

export enum MetrikaClickFromParam {
    UNAVAILABLE_HGZ = 'unavailable_hgz', // Тизер урока
    CALENDAR_PLAN = 'calendar_plan', // Календарный план
}

function reachGoal(goal: string, params: MetrikaParams) {
    ym('reachGoal', goal, params);
}

export const useMetrika = (startDate: Date | null) => {
    const routeParams = useParams<CalendarPlanShowcaseRouteParams>();
    const { classId: classIdFromRoute, subject: subjectSlugFromRoute } = routeParams;

    const courseId = useSelector(getCurrentYearCourseId);

    const classId = isNaN(Number(classIdFromRoute)) ? null : Number(classIdFromRoute);
    const team = useSelector<ReduxTeacherStore, Team | null>((store) => getCurrentClass(store, { classId }));
    const teamLevel = team?.level;

    const teacher = useSelector<ReduxTeacherStore, Me | null>((store) => getMe(store));
    const teacherId = teacher?.id;

    const fromDateISO8601 = startDate ? format(startDate, 'yyyy-MM-dd') : '';

    const commonMetrikaParams = useMemo(
        () => ({
            course_id: courseId || undefined,
            period_id: fromDateISO8601,
            subject_slug: subjectSlugFromRoute,
            teacher_id: String(teacherId),
            team_id: classId || undefined,
            team_level: String(teamLevel),
            template_id: '-1',
        }),
        [classId, courseId, fromDateISO8601, subjectSlugFromRoute, teacherId, teamLevel]
    );

    const cardMetrikaParams = useMemo(
        () => ({
            ...commonMetrikaParams,
            block_type: MetrikaBlockTypeParam.ARRAY_OF_LESSONS,
            lesson_type: MetrikaBlockTypeParam.ARRAY_OF_LESSONS,
            main_block_number: '1',
            main_block_name: MetrikaBlockNameParam.MAIN_THEME,
            main_element_number: '1',
        }),
        [commonMetrikaParams]
    );

    const reachCalendarPlanButtonClickGoal = useCallback(
        (itemId?: number | string) => {
            reachGoal('teacher_vitrina_click_plan_calendar', {
                ...commonMetrikaParams,
                lesson_id: String(itemId),
            });
        },
        [commonMetrikaParams]
    );

    const reachCalendarPlanThemeNameClickGoal = useCallback(
        (itemId: number) => {
            reachGoal('teacher_vitrina_calendar_chose_period', {
                ...commonMetrikaParams,
                lesson_id: String(itemId),
            });
        },
        [commonMetrikaParams]
    );

    const reachNextLessonClickGoal = useCallback(
        (itemId: number | string) => {
            reachGoal('teacher_vitrina_shift_carousel', {
                ...cardMetrikaParams,
                lesson_id: String(itemId),
            });
        },
        [cardMetrikaParams]
    );

    const reachPrevLessonClickGoal = useCallback(
        (itemId: number | string) => {
            reachGoal('teacher_vitrina_shift_carousel', {
                ...cardMetrikaParams,
                lesson_id: String(itemId),
            });
        },
        [cardMetrikaParams]
    );

    const reachLessonGiveButtonClick = useCallback(
        (itemId: number) => {
            reachGoal('teacher_vitrina_click_prepared_lesson', {
                ...cardMetrikaParams,
                lesson_id: String(itemId),
            });
        },
        [cardMetrikaParams]
    );

    const reachUploadCalendarButtonClick = useCallback(
        (clickFrom: MetrikaClickFromParam) => {
            reachGoal('teacher_click_load_own_umk', {
                ...cardMetrikaParams,
                click_from: clickFrom,
            });
        },
        [cardMetrikaParams]
    );

    return {
        reachCalendarPlanButtonClickGoal,
        reachCalendarPlanThemeNameClickGoal,
        reachLessonGiveButtonClick,
        reachNextLessonClickGoal,
        reachPrevLessonClickGoal,
        reachUploadCalendarButtonClick,
    };
};
